import React from 'react';
import PropTypes from 'prop-types';
import OverviewTab from './OverviewTab';
import BusinessContent from './BusinessContent';
import MetricCard from './MetricCard';

const TabContent = ({ activeTab, analysisData }) => {
    if (!analysisData) return null;

    const renderMediaTab = () => {
        // Get images from either productImages (scanner response) or images (database response)
        const allProductImages = analysisData.productImages?.images || analysisData.images?.images || [];
        const homepageImages = analysisData.productImages?.images || [];

        // Deduplicate by base URL (strip query params)
        const seen = new Set();
        const uniqueImages = allProductImages.filter(img => {
            const baseUrl = img.url?.split('?')[0];
            if (!baseUrl || seen.has(baseUrl)) return false;
            seen.add(baseUrl);
            return true;
        });

        const withAlt = uniqueImages.filter(img => img.alt).length;
        const withoutAlt = uniqueImages.length - withAlt;
        const visibleImgs = uniqueImages.filter(img => img.strategy === 'visible-img');
        const lazyImgs = uniqueImages.filter(img => img.strategy === 'lazy-loaded');

        return (
            <div className="tab-content">
                <div className="metric-grid">
                    <MetricCard title="Image Analysis">
                        <p><strong>Total Unique Images:</strong> {uniqueImages.length}</p>
                        <p><strong>With Alt Text:</strong> {withAlt}</p>
                        <p><strong>Without Alt Text:</strong> {withoutAlt}</p>
                        <p><strong>Visible Images:</strong> {visibleImgs.length}</p>
                        <p><strong>Lazy Loaded:</strong> {lazyImgs.length}</p>
                    </MetricCard>
                    <MetricCard title="Logo Detection">
                        <p><strong>Logo Found:</strong> {analysisData.logo?.url ? 'Yes' : 'No'}</p>
                        <p><strong>Logo Type:</strong> {analysisData.logo?.type || 'N/A'}</p>
                        <p><strong>Detection Strategy:</strong> {analysisData.logo?.strategy || 'N/A'}</p>
                    </MetricCard>
                    <MetricCard title="Homepage Images">
                        <p><strong>Total:</strong> {analysisData.productImages?.total || 0}</p>
                        {homepageImages.length > 0 ? (
                            homepageImages.slice(0, 3).map((img) => (
                                <p key={`img-${img.url}`}><strong>{img.strategy}:</strong> {img.title || img.alt || 'Untitled'}</p>
                            ))
                        ) : (
                            <p style={{ color: '#94a3b8', fontSize: '13px' }}>No images detected</p>
                        )}
                    </MetricCard>
                </div>

                {analysisData.logo?.url && (
                    <div className="logo-section">
                        <h4>Detected Logo</h4>
                        <div className="logo-grid">
                            <div className="logo-item">
                                <img
                                    src={analysisData.logo.url}
                                    alt="Site Logo"
                                    className="logo-img"
                                    style={{ maxHeight: '80px', objectFit: 'contain' }}
                                />
                                <p style={{ fontSize: '12px', color: '#64748b', margin: '0' }}>Strategy: {analysisData.logo.strategy}</p>
                            </div>
                        </div>
                    </div>
                )}

                {uniqueImages.length > 0 && (
                    <div className="product-gallery">
                        <h4>Product Images ({uniqueImages.length})</h4>
                        <div className="product-grid">
                            {uniqueImages.slice(0, 30).map((img, index) => (
                                <div className="product-card" key={img.url || `product-${index}`}>
                                    <img
                                        src={img.url}
                                        alt={img.alt || `Image ${index + 1}`}
                                        className="product-img"
                                        loading="lazy"
                                    />
                                    <div className="product-card-meta">
                                        {img.alt && <span className="product-meta-tag" title={img.alt}>{img.alt.substring(0, 40)}</span>}
                                        <span className="product-meta-tag">{img.strategy}</span>
                                        {img.width && img.height && (
                                            <span className="product-meta-tag">{img.width}×{img.height}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDesignTab = () => {
        const colors = analysisData.themeColors || {};

        // Extract top 5 colors from all available sources
        const getTop5Colors = () => {
            const colorSet = new Set();

            // Add individual color properties (in priority order)
            if (colors.primary) colorSet.add(colors.primary);
            if (colors.secondary) colorSet.add(colors.secondary);
            if (colors.accent) colorSet.add(colors.accent);
            if (colors.background) colorSet.add(colors.background);
            if (colors.text) colorSet.add(colors.text);
            if (colors.metaTheme) colorSet.add(colors.metaTheme);

            // Add from allBackgrounds (filter valid hex colors only)
            if (Array.isArray(colors.allBackgrounds)) {
                colors.allBackgrounds.forEach(color => {
                    if (color && /^#[0-9a-f]{3,6}$/i.test(color)) {
                        colorSet.add(color);
                    }
                });
            }

            // Add from allTextColors (filter valid hex colors only)
            if (Array.isArray(colors.allTextColors)) {
                colors.allTextColors.forEach(color => {
                    if (color && /^#[0-9a-f]{3,6}$/i.test(color)) {
                        colorSet.add(color);
                    }
                });
            }

            // Add from allBorderColors (filter valid hex colors only)
            if (Array.isArray(colors.allBorderColors)) {
                colors.allBorderColors.forEach(color => {
                    if (color && /^#[0-9a-f]{3,6}$/i.test(color)) {
                        colorSet.add(color);
                    }
                });
            }

            // Convert set to array and return first 5
            return Array.from(colorSet).slice(0, 5);
        };

        const topColors = getTop5Colors();

        return (
            <div className="tab-content">
                <div className="metric-grid">
                    <MetricCard title="Brand Colors (Top 5)">
                        {topColors.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {topColors.map((color, index) => (
                                    <div key={`color-${index}-${color}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <button
                                            type="button"
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                backgroundColor: color,
                                                borderRadius: '6px',
                                                border: '2px solid #e2e8f0',
                                                flexShrink: 0,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                                padding: 0
                                            }}
                                            title={`Click to copy ${color}`}
                                            aria-label={`Copy color ${color}`}
                                            onClick={() => navigator.clipboard.writeText(color)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                        ></button>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ margin: '0 0 0.25rem 0', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                                                Color {index + 1}
                                            </p>
                                            <p style={{ margin: '0', fontSize: '14px', fontWeight: '600', fontFamily: 'monospace' }}>
                                                {color}
                                            </p>
                                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
                                                Click to copy
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0' }}>No colors detected</p>
                        )}
                    </MetricCard>

                    <MetricCard title="Typography">
                        <div>
                            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '14px', color: '#0f172a' }}>Brand Typography</h4>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 1rem 0', lineHeight: '1.6' }}>
                                Generic sans-serif typography system detected. The brand uses standard web-safe typefaces for optimal performance and consistency across devices.
                            </p>
                            <div style={{ padding: '0.75rem', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                <p style={{ fontSize: '13px', margin: '0', color: '#475569' }}>
                                    Font: <strong>System sans-serif (Segoe UI, Roboto, -apple-system)</strong>
                                </p>
                            </div>
                        </div>
                    </MetricCard>
                </div>
            </div>
        );
    };

    const renderOverviewTab = () => <OverviewTab analysisData={analysisData} />;

    const renderBusinessTab = () => <BusinessContent analysisData={analysisData} />;

    const renderTabContent = () => {
        const tabs = {
            overview: renderOverviewTab,
            business: renderBusinessTab,
            design: renderDesignTab,
            media: renderMediaTab
        };

        const renderFunction = tabs[activeTab];
        return renderFunction ? renderFunction() : null;
    };

    return renderTabContent();
};

TabContent.propTypes = {
    activeTab: PropTypes.string.isRequired,
    analysisData: PropTypes.object
};

export default TabContent;
